import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, Input, Button, Typography, Avatar, message, List, Spin, Empty } from 'antd';
import {
  SendOutlined,
  UserOutlined,
  CameraOutlined,
  VideoCameraOutlined,
  AudioOutlined,
  EnvironmentOutlined,
  CloseOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { chatService, storageUrl } from '../services/api';
import apiClient from '../services/api';
import { extractList, extractObject } from '../utils/formatters';
import './CustomerDashboard.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Media paths come back relative (e.g. "/storage/chat/..."). `storageUrl`
// resolves them against the configured API host; hardcoding a localhost port
// here broke every image, video and voice message in production.
const getFullUrl = (url) => storageUrl(url) || '';

const Chat = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [pendingMedia, setPendingMedia] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('authUser') || '{}');
    setCurrentUser(user);
  }, []);

  const location = useLocation();

  const stateContactId = location.state?.contactId;
  const stateContactName = location.state?.contactName;

  useEffect(() => {
    fetchConversations().then((list) => {
      if (!stateContactId) return;
      const existing = list.find((c) => c.contact_id === stateContactId);
      if (existing) {
        selectConversation(existing);
      } else {
        const newConv = {
          contact_id: stateContactId,
          contact_name: stateContactName || 'Provider',
          contact_image: null,
          contact_type: 'Provider',
          last_message: '',
          unread_count: 0,
        };
        setConversations((prev) => [newConv, ...prev]);
        selectConversation(newConv);
      }
      window.history.replaceState({}, document.title);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await chatService.getConversations();
      setConversations(extractList(response.data));
      return extractList(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }
  };

  const fetchMessages = useCallback(async (contactId) => {
    if (!contactId) return [];
    setLoading(true);
    try {
      const response = await chatService.getChatMessages(contactId);
      // The endpoint returns a bare array today but is wrapped in `{ data }` in
      // some deployments; extractList handles both and never yields an object
      // that would break the `.map` below.
      const msgs = extractList(response.data);
      setMessages(msgs);
      return msgs;
    } catch (error) {
      console.error('Error fetching messages:', error);
      message.error('Failed to load messages');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const selectConversation = async (conversation) => {
    setSelectedContact(conversation);
    setPendingMedia(null);
    setIsRecording(false);
    const msgs = await fetchMessages(conversation.contact_id);

    const unreadIds = msgs
      .filter((m) => m.receiver_id === currentUser?.id && !m.read_at)
      .map((m) => m.id);
    if (unreadIds.length > 0) {
      try {
        await chatService.markMessagesAsRead({ message_ids: unreadIds });
        fetchConversations();
      } catch {
        console.error('Failed to mark messages as read');
      }
    }
  };

  const handleMediaChange = (type) => (file) => {
    setPendingMedia({ type, file, preview: URL.createObjectURL(file) });
    return false;
  };

  const handleCancelMedia = () => {
    setPendingMedia(null);
  };

  const startRecording = async () => {
    if (!selectedContact) {
      message.warning('Select a conversation first');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = () => {
          setPendingMedia({ type: 'voice', file: audioBlob, preview: reader.result });
        };
        reader.readAsDataURL(audioBlob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch {
      message.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    }
    setRecordingDuration(0);
  };

  const handleLocationShare = () => {
    if (!selectedContact) {
      message.warning('Select a conversation first');
      return;
    }
    if (!navigator.geolocation) {
      message.error('Geolocation is not supported by your browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        setSending(true);
        try {
          const response = await chatService.sendMessage({
            receiver_id: selectedContact.contact_id,
            message: 'Shared location',
            location_lat: position.coords.latitude,
            location_lng: position.coords.longitude,
            location_name: 'Current Location',
          });
          const created = extractObject(response.data);
          if (created) setMessages((prev) => [...prev, created]);
        } catch {
          message.error('Failed to share location');
        } finally {
          setSending(false);
        }
      },
      () => message.error('Unable to get your location'),
    );
  };

  const handleSendMessage = async () => {
    if (!selectedContact) return;

    if (pendingMedia) {
      setSending(true);
      try {
        const formData = new FormData();
        const fieldMap = { image: 'image_url', video: 'video_url', voice: 'voice_url' };
        formData.append(fieldMap[pendingMedia.type], pendingMedia.file);
        formData.append('receiver_id', selectedContact.contact_id);
        formData.append(
          'message',
          pendingMedia.type === 'image'
            ? 'Image'
            : pendingMedia.type === 'video'
              ? 'Video'
              : 'Voice message',
        );

        const response = await apiClient.post('/chat-messages', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const created = extractObject(response.data);
        if (created) setMessages((prev) => [...prev, created]);
        setPendingMedia(null);
      } catch {
        message.error('Failed to send media');
      } finally {
        setSending(false);
      }
      return;
    }

    if (!messageInput.trim()) return;
    setSending(true);
    try {
      const response = await chatService.sendMessage({
        receiver_id: selectedContact.contact_id,
        message: messageInput.trim(),
      });
      const created = extractObject(response.data);
      if (created) setMessages((prev) => [...prev, created]);
      setMessageInput('');
    } catch {
      message.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString();
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const renderMessageContent = (msg, isMine) => {
    const isImage = msg.message_type === 'image';
    const isVideo = msg.message_type === 'video';
    const isVoice = msg.message_type === 'voice';
    const isLocation = msg.message_type === 'location';
    const textColor = isMine ? '#ffffff' : '#374151';

    if (isImage) {
      const imgUrl = msg.image_url || msg.message;
      return (
        <div>
          <img
            src={getFullUrl(imgUrl)}
            alt="Sent"
            style={{ maxWidth: 260, borderRadius: 10, display: 'block' }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          {msg.message && msg.message !== 'Image' && (
            <Text style={{ color: textColor, fontSize: 14, display: 'block', marginTop: 4 }}>
              {msg.message}
            </Text>
          )}
        </div>
      );
    }

    if (isVideo) {
      const vidUrl = msg.video_url || msg.message;
      return (
        <div>
          <video
            controls
            style={{ maxWidth: 260, maxHeight: 200, borderRadius: 10, display: 'block' }}
          >
            <source src={getFullUrl(vidUrl)} />
          </video>
          {msg.message && msg.message !== 'Video' && (
            <Text style={{ color: textColor, fontSize: 14, display: 'block', marginTop: 4 }}>
              {msg.message}
            </Text>
          )}
        </div>
      );
    }

    if (isVoice) {
      const audioUrl = msg.voice_url || msg.message;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <audio controls style={{ maxWidth: 200, height: 36 }}>
            <source src={getFullUrl(audioUrl)} />
          </audio>
        </div>
      );
    }

    if (isLocation) {
      let locationData = msg.location_data;
      if (typeof locationData === 'string') {
        try {
          locationData = JSON.parse(locationData);
        } catch {
          locationData = null;
        }
      }
      const lat = locationData?.latitude;
      const lng = locationData?.longitude;
      const address = locationData?.address || locationData?.name || 'Shared Location';
      const mapUrl = lat && lng ? `https://maps.google.com/?q=${lat},${lng}` : null;

      return (
        <div style={{ padding: 8, background: '#e8f4fb', borderRadius: 8, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <EnvironmentOutlined style={{ fontSize: 24, color: '#2e9bd6' }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#2e7eba' }}>Location</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{address}</div>
              {mapUrl && (
                <a
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 11, color: '#2e7eba', marginTop: 4, display: 'inline-block' }}
                >
                  View on map
                </a>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <Text style={{ color: textColor, fontSize: 14, whiteSpace: 'pre-wrap' }}>{msg.message}</Text>
    );
  };

  return (
    <div className="customer-page" style={{ padding: 0, maxWidth: 900 }}>
      <div className="customer-page-header">
        <Title level={2}>Chat</Title>
        <Text style={{ color: '#6b7280' }}>Communicate with providers and support team.</Text>
      </div>

      <Card
        className="customer-card"
        styles={{
          body: { padding: 0, display: 'flex', height: 'calc(100vh - 240px)', minHeight: 500 },
        }}
      >
        <div
          style={{
            width: 280,
            borderRight: '1px solid #e8f0e8',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ padding: '16px 16px 8px', borderBottom: '1px solid #e8f0e8' }}>
            <Text strong style={{ color: '#2d5a3d', fontSize: 15 }}>
              Conversations
            </Text>
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {conversations.length === 0 ? (
              <Empty
                description="No conversations yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: 32 }}
              />
            ) : (
              <List
                dataSource={conversations}
                renderItem={(conv) => (
                  <List.Item
                    onClick={() => selectConversation(conv)}
                    style={{
                      cursor: 'pointer',
                      padding: '12px 16px',
                      borderBottom: '1px solid #f3f4f6',
                      background:
                        selectedContact?.contact_id === conv.contact_id ? '#fafff5' : 'transparent',
                      transition: 'background 0.2s',
                    }}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          src={getFullUrl(conv.contact_image)}
                          icon={<UserOutlined />}
                          style={{ backgroundColor: '#52b788' }}
                        />
                      }
                      title={
                        <Text strong style={{ color: '#2d5a3d', fontSize: 14 }}>
                          {conv.contact_name || 'Unknown'}
                          {conv.unread_count > 0 && (
                            <span
                              style={{
                                marginLeft: 8,
                                background: '#52b788',
                                color: '#fff',
                                borderRadius: 10,
                                padding: '1px 8px',
                                fontSize: 11,
                              }}
                            >
                              {conv.unread_count}
                            </span>
                          )}
                        </Text>
                      }
                      description={
                        <div>
                          <Text
                            style={{ color: '#9ca3af', fontSize: 12, display: 'block' }}
                            numberOfLines={1}
                          >
                            {conv.last_message || 'No messages yet'}
                          </Text>
                          <Text style={{ color: '#d1d5db', fontSize: 11 }}>
                            {conv.last_message_time ? formatTime(conv.last_message_time) : ''}
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {!selectedContact ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 12,
                color: '#9ca3af',
              }}
            >
              <UserOutlined style={{ fontSize: 48, opacity: 0.3 }} />
              <Text style={{ color: '#9ca3af' }}>Select a conversation to start chatting</Text>
            </div>
          ) : (
            <>
              <div
                style={{
                  padding: '12px 20px',
                  borderBottom: '1px solid #e8f0e8',
                  background: '#fafff5',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <Avatar
                  src={selectedContact.contact_image}
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#52b788' }}
                />
                <div>
                  <Text strong style={{ color: '#2d5a3d' }}>
                    {selectedContact.contact_name}
                  </Text>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    {selectedContact.contact_type || 'User'}
                    {selectedContact.order_id && ` | Order #${selectedContact.order_id}`}
                  </div>
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  overflow: 'auto',
                  padding: 16,
                  background: '#f9fafb',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                {loading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin />
                  </div>
                ) : messages.length === 0 ? (
                  <Empty
                    description="No messages"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: 40 }}
                  />
                ) : (
                  messages.map((msg) => {
                    // Ids can be numbers or strings depending on the payload shape.
                    const isMine =
                      msg.sender_id != null &&
                      currentUser?.id != null &&
                      String(msg.sender_id) === String(currentUser.id);
                    return (
                      <div
                        key={msg.id}
                        style={{
                          display: 'flex',
                          justifyContent: isMine ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <div
                          style={{
                            maxWidth: '70%',
                            background: isMine
                              ? 'linear-gradient(135deg, #52b788, #3d7a4d)'
                              : '#fff',
                            padding:
                              msg.message_type === 'image' || msg.message_type === 'video'
                                ? '6px'
                                : '10px 14px',
                            borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                            boxShadow: isMine
                              ? '0 2px 6px rgba(45,90,61,0.2)'
                              : '0 1px 3px rgba(0,0,0,0.06)',
                            border: isMine ? 'none' : '1px solid #e8f0e8',
                          }}
                        >
                          {renderMessageContent(msg, isMine)}
                          <div
                            style={{
                              fontSize: 11,
                              color: isMine ? 'rgba(255,255,255,0.75)' : '#9ca3af',
                              marginTop: 4,
                              textAlign: 'right',
                            }}
                          >
                            {msg.created_at ? formatTime(msg.created_at) : ''}
                            {msg.read_at && isMine && (
                              <span style={{ color: '#d9f2e4', marginLeft: 4 }}>Read</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {pendingMedia && (
                <div
                  style={{
                    padding: '8px 16px',
                    borderTop: '1px solid #e8f0e8',
                    background: '#fafff5',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  {pendingMedia.type === 'image' && (
                    <img
                      src={pendingMedia.preview}
                      alt="Preview"
                      style={{ height: 48, borderRadius: 6 }}
                    />
                  )}
                  {pendingMedia.type === 'video' && (
                    <VideoCameraOutlined style={{ fontSize: 32, color: '#52b788' }} />
                  )}
                  {pendingMedia.type === 'voice' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <AudioOutlined style={{ fontSize: 20, color: '#52b788' }} />
                      <Text style={{ fontSize: 13, color: '#374151' }}>
                        Voice message ({Math.round(pendingMedia.file.size / 1024)}KB)
                      </Text>
                    </div>
                  )}
                  <Text style={{ flex: 1, fontSize: 13, color: '#6b7280' }}>
                    {pendingMedia.type === 'image'
                      ? 'Image ready to send'
                      : pendingMedia.type === 'video'
                        ? 'Video ready to send'
                        : 'Voice ready to send'}
                  </Text>
                  <Button size="small" icon={<CloseOutlined />} onClick={handleCancelMedia} />
                </div>
              )}

              {isRecording && (
                <div
                  style={{
                    padding: '8px 16px',
                    borderTop: '1px solid #e8f0e8',
                    background: '#fdecec',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: '#e5484d',
                      animation: 'pulse 1s infinite',
                    }}
                  />
                  <Text style={{ color: '#e5484d', fontWeight: 600 }}>
                    Recording... {formatDuration(recordingDuration)}
                  </Text>
                  <Button
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={stopRecording}
                    style={{ background: '#52b788', color: '#fff', borderColor: '#52b788' }}
                  >
                    Stop
                  </Button>
                  <Button size="small" icon={<CloseOutlined />} onClick={cancelRecording}>
                    Cancel
                  </Button>
                </div>
              )}

              <div
                style={{
                  padding: '12px 16px',
                  borderTop: '1px solid #e8f0e8',
                  background: '#fff',
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-end',
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  ref={imageInputRef}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleMediaChange('image')(file);
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
                <input
                  type="file"
                  accept="video/*"
                  ref={videoInputRef}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleMediaChange('video')(file);
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />

                <Button
                  icon={<CameraOutlined />}
                  onClick={() => imageInputRef.current?.click()}
                  style={{ borderRadius: 10, color: '#52b788', borderColor: '#52b788' }}
                />
                <Button
                  icon={<VideoCameraOutlined />}
                  onClick={() => videoInputRef.current?.click()}
                  style={{ borderRadius: 10, color: '#52b788', borderColor: '#52b788' }}
                />
                <Button
                  icon={<AudioOutlined />}
                  onClick={startRecording}
                  disabled={isRecording}
                  style={{ borderRadius: 10, color: '#52b788', borderColor: '#52b788' }}
                />
                <Button
                  icon={<EnvironmentOutlined />}
                  onClick={handleLocationShare}
                  style={{ borderRadius: 10, color: '#52b788', borderColor: '#52b788' }}
                />

                <TextArea
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Type your message..."
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  style={{ borderRadius: 10, flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  loading={sending}
                  disabled={!messageInput.trim() && !pendingMedia}
                  style={{
                    borderRadius: 10,
                    height: 38,
                    background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
                    border: 'none',
                  }}
                >
                  Send
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Chat;
